import { createSignal, onMount, For, Show } from 'solid-js';
import { useVisibilityPoll } from '../hooks/useVisibilityPoll';
import { useApi, isOk } from '../api';
import type { BotLicenseAssignmentDto } from '../api';
import { firstError } from '../api/envelope';
import Text from '../components/Text';
import BotLicenseAssignmentList from '../components/BotLicenseAssignmentList';
import { showConfirm } from '../components/Dialog';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { refreshBotLicenses } from '../hooks/useBotLicense';
import { refreshLicenses } from '../hooks/useLicense';
import { BOT_LICENSE_NAME_KEYS, type UserGrantSummaryDto } from '../services/grantsApi';
import { formatDateTime } from '../services/dateFormat';

import votingStyles from './Voting.module.css';

export default function MyLicenses() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const [summary, setSummary] = createSignal<UserGrantSummaryDto | null>(null);
    const [seats, setSeats] = createSignal<BotLicenseAssignmentDto[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [releasingId, setReleasingId] = createSignal<string | null>(null);

    const fetchSummary = async () => {
        const [grantsRes, seatsRes] = await Promise.all([api.grants.me(), api.botLicense.me()]);
        if (isOk(grantsRes.data)) setSummary(grantsRes.data.value as unknown as UserGrantSummaryDto);
        if (isOk(seatsRes.data)) setSeats(seatsRes.data.value.seats);
        setLoading(false);
    };

    const unassign = async (assignment: BotLicenseAssignmentDto) => {
        if (!assignment.botRegistryId) return;
        const licenseName = t(BOT_LICENSE_NAME_KEYS[assignment.licenseType]);
        if (!await showConfirm(t('BOT_LICENSE_UNASSIGN_CONFIRM', licenseName, assignment.botName ?? ''), true, { confirmLabel: t('BOT_LICENSE_UNASSIGN') })) return;
        setReleasingId(assignment.id);
        try {
            const res = await api.botLicense.detach(assignment.botRegistryId, assignment.licenseType);
            if (!isOk(res.data)) { toast?.error(firstError(res.data) ?? t('BOT_LICENSE_UNASSIGN_FAILED')); return; }
            refreshBotLicenses();
            refreshLicenses();
            await fetchSummary();
        } finally {
            setReleasingId(null);
        }
    };

    onMount(() => {
        void fetchSummary();
        useVisibilityPoll(() => void fetchSummary(), 30_000);
    });

    const counts = () => summary()?.entries.filter(e => e.kind === 'Count') ?? [];
    const botLicenses = () => summary()?.entries.filter(e => e.kind === 'Seat') ?? [];
    const licenses = () => summary()?.entries.filter(e => e.kind === 'License') ?? [];

    return (
        <div class={votingStyles.container}>
            <div class={votingStyles.headerRow}>
                <h1><Text val="MY_LICENSES_TITLE" /></h1>
            </div>

            <Show when={!loading()} fallback={<div class={votingStyles.list}><div class={votingStyles.skeleton} /><div class={votingStyles.skeleton} /></div>}>
                <h2><Text val="MY_LICENSES_QUOTAS" /></h2>
                <div class="v" style={{ gap: 'var(--spacing-md)', 'margin-bottom': 'var(--spacing-2xl)' }}>
                    <For each={counts()}>{(e) => (
                        <div class="fcard">
                            <div class="h" style={{ 'justify-content': 'space-between', 'align-items': 'center', 'flex-wrap': 'wrap', gap: 'var(--spacing-md)' }}>
                                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                    <span style={{ 'font-weight': 600 }}>{e.displayName}</span>
                                    <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>{e.description}</span>
                                </div>
                                <div style={{ 'font-family': 'var(--font-family-mono)', 'font-size': 'var(--font-size-xl)', 'font-weight': 600 }}>
                                    <span style={{ color: e.used >= e.effectiveValue ? 'var(--error-500)' : 'var(--success-500)' }}>{e.used}</span>
                                    <span style={{ color: 'var(--dark-text-muted)' }}> / {e.effectiveValue}</span>
                                </div>
                            </div>
                            <Show when={e.sources.length > 0}>
                                <div style={{ 'margin-top': 'var(--spacing-md)', color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>
                                    <Text val="MY_LICENSES_SOURCES" />:
                                    <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0 }}>
                                        <For each={e.sources}>{(src) => (
                                            <li>
                                                +{src.value}
                                                <Show when={src.sourceTenantName} fallback={
                                                    <span style={{ color: 'var(--accent-purple)' }}> <Text val="MY_LICENSES_DIRECT" /></span>
                                                }>
                                                    <span> ({src.sourceTenantName})</span>
                                                </Show>
                                                <span style={{ 'margin-left': 'var(--spacing-sm)' }}>{formatDateTime(src.createdAt)}</span>
                                                <Show when={src.note}><span style={{ 'margin-left': 'var(--spacing-sm)', 'font-style': 'italic' }}>"{src.note}"</span></Show>
                                            </li>
                                        )}</For>
                                    </ul>
                                </div>
                            </Show>
                        </div>
                    )}</For>
                </div>

                <h2><Text val="BOT_LICENSES_TITLE" /></h2>
                <p style={{ color: 'var(--dark-text-muted)', 'margin-top': 0 }}><Text val="BOT_LICENSES_HINT" /></p>
                <div class="v" style={{ gap: 'var(--spacing-md)', 'margin-bottom': 'var(--spacing-2xl)' }}>
                    <For each={botLicenses()}>{(e) => (
                        <div class="fcard">
                            <div class="h" style={{ 'justify-content': 'space-between', 'align-items': 'center', 'flex-wrap': 'wrap', gap: 'var(--spacing-md)' }}>
                                <span style={{ 'font-weight': 600 }}><Text val={BOT_LICENSE_NAME_KEYS[e.type]} /></span>
                                <div style={{ 'font-family': 'var(--font-family-mono)', 'font-size': 'var(--font-size-xl)', 'font-weight': 600 }}>
                                    <span style={{ color: e.effectiveValue > 0 && e.used >= e.effectiveValue ? 'var(--error-500)' : 'var(--success-500)' }}>{e.used}</span>
                                    <span style={{ color: 'var(--dark-text-muted)' }}> / {e.effectiveValue}</span>
                                </div>
                            </div>
                            <BotLicenseAssignmentList
                                assignments={seats().filter(seat => seat.licenseType === e.type)}
                                actions={(assignment) => (
                                    <Show when={assignment.state !== 'Cooldown'}>
                                        <button class="btn btn-secondary btn-sm" disabled={releasingId() === assignment.id} onClick={() => void unassign(assignment)}>
                                            <Text val="BOT_LICENSE_UNASSIGN" />
                                        </button>
                                    </Show>
                                )}
                            />
                        </div>
                    )}</For>
                </div>

                <h2><Text val="MY_LICENSES_LICENSES" /></h2>
                <div class="v" style={{ gap: 'var(--spacing-md)' }}>
                    <For each={licenses()}>{(e) => (
                        <div class="fcard h" style={{ 'justify-content': 'space-between', 'align-items': 'center', 'flex-wrap': 'wrap', gap: 'var(--spacing-md)' }}>
                            <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span style={{ 'font-weight': 600 }}>{e.displayName}</span>
                                <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>{e.description}</span>
                                <Show when={e.sources.length > 0}>
                                    <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-xs)' }}>
                                        <For each={e.sources}>{(src) => (
                                            <span style={{ 'margin-right': 'var(--spacing-md)' }}>
                                                <Show when={src.sourceTenantName} fallback={<><Text val="MY_LICENSES_DIRECT" /></>}>
                                                    <span>{src.sourceTenantName}</span>
                                                </Show>
                                                {' · '}{formatDateTime(src.createdAt)}
                                            </span>
                                        )}</For>
                                    </span>
                                </Show>
                            </div>
                            <span class={votingStyles.outcomeBadge + ' ' + (e.effectiveValue > 0 ? votingStyles.outcomeOk : '')}>
                                <Text val={e.effectiveValue > 0 ? 'MY_LICENSES_GRANTED' : 'MY_LICENSES_NOT_GRANTED'} />
                            </span>
                        </div>
                    )}</For>
                </div>
            </Show>
        </div>
    );
}
